import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

/**
 * Premium animated "Sentetica" hero typography.
 * - Large gradient text with very slow breathing wave
 * - Mouse-parallax across multiple glassmorphism layers at different depths
 * - Apple/Stripe-level minimalism
 */
const SenteticaHeroText = () => {
  const containerRef = useRef(null);
  const [active, setActive] = useState(false);

  // Mouse motion values — smoothed with springs so motion is silky
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 40, damping: 20, mass: 0.6 });
  const smoothY = useSpring(mouseY, { stiffness: 40, damping: 20, mass: 0.6 });

  // Different parallax depths per layer
  const orb1X = useTransform(smoothX, (v) => v * 30);
  const orb1Y = useTransform(smoothY, (v) => v * 20);
  const orb2X = useTransform(smoothX, (v) => v * -40);
  const orb2Y = useTransform(smoothY, (v) => v * -25);
  const orb3X = useTransform(smoothX, (v) => v * 18);
  const orb3Y = useTransform(smoothY, (v) => v * 28);
  const textX = useTransform(smoothX, (v) => v * 8);
  const textY = useTransform(smoothY, (v) => v * 6);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
      mouseX.set(x);
      mouseY.set(y);
    };
    const onEnter = () => setActive(true);
    const onLeave = () => {
      setActive(false);
      mouseX.set(0);
      mouseY.set(0);
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [mouseX, mouseY]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      data-testid="sentetica-hero-text"
    >
      {/* Frosted glass shapes — depth layers with different parallax speeds */}
      <motion.div
        aria-hidden="true"
        style={{ x: orb1X, y: orb1Y }}
        className="absolute top-[8%] left-[6%] w-[420px] h-[420px] rounded-full blur-[110px]"
      >
        <div
          className="w-full h-full rounded-full"
          style={{ background: 'radial-gradient(circle at 30% 30%, rgba(133,158,255,0.55), rgba(133,158,255,0) 70%)' }}
        />
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ x: orb2X, y: orb2Y }}
        className="absolute top-[12%] right-[4%] w-[520px] h-[520px] rounded-full blur-[130px]"
      >
        <div
          className="w-full h-full rounded-full"
          style={{ background: 'radial-gradient(circle at 60% 40%, rgba(192,160,255,0.52), rgba(192,160,255,0) 70%)' }}
        />
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ x: orb3X, y: orb3Y }}
        className="absolute bottom-[4%] left-[34%] w-[400px] h-[400px] rounded-full blur-[100px]"
      >
        <div
          className="w-full h-full rounded-full"
          style={{ background: 'radial-gradient(circle at 50% 50%, rgba(160,200,255,0.5), rgba(160,200,255,0) 70%)' }}
        />
      </motion.div>

      {/* Frosted glass card behind text — subtle depth */}
      <motion.div
        aria-hidden="true"
        animate={{
          scale: [1, 1.015, 1],
          opacity: [0.6, 0.75, 0.6],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute w-[88%] max-w-[1400px] h-[42%] rounded-[40px]"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.2) 100%)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.6)',
          boxShadow: '0 30px 80px -20px rgba(120,140,200,0.18)',
        }}
      />

      {/* Subtle dot grid for tech texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.045]"
        style={{
          backgroundImage: 'radial-gradient(circle, #6b7280 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Main animated "Sentetica" text */}
      <motion.h2
        aria-label="Sentetica"
        style={{ x: textX, y: textY }}
        animate={{
          y: [0, -10, 0, 10, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="relative font-heading font-black leading-none select-none text-center"
      >
        <span
          className="block"
          style={{
            fontSize: 'clamp(80px, 16vw, 280px)',
            background:
              'linear-gradient(135deg, #0A0A0A 0%, #1f2740 35%, #3d4a8a 55%, #8a7ad6 78%, #b794f4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 30px 60px rgba(100,120,200,0.12))',
            letterSpacing: '-0.04em',
          }}
        >
          Sentetica
        </span>
      </motion.h2>

      {/* Hint indicator on first interaction */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: active ? 0.55 : 0 }}
        transition={{ duration: 0.6 }}
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-[#0A0A0A]/40 text-[10px] uppercase tracking-[0.3em]"
      />
    </div>
  );
};

export default SenteticaHeroText;
